import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, Plus, ListChecks, Palette, ChevronDown, ChevronRight, GripVertical, Pencil, Check, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useClient } from "@/context/ClientContext";
import { GROUP_META, GROUP_ORDER, groupName } from "@/lib/leadGroups";

/** Tenant-scoped Dropdown Catalogue manager for Ooredoo / Al-Meera settings.
 *  Dispositions are organised into the P1–P4 ownership groups (PRD v2.0 §3):
 *  group membership is mandatory, drag-and-drop sets the order agents see, and
 *  moving a disposition between groups asks for confirmation. */
const CATALOGUE_KEYS = [
  { key: "sector", label: "Sector" },
  { key: "employee_count", label: "Employee Count" },
  { key: "disposition", label: "Disposition" },
  { key: "call_language", label: "Call Language" },
];
// Campaign tenants (leads module on a ticketing client, e.g. Starlink) manage the campaign lists instead.
const CAMPAIGN_KEYS = [
  { key: "lead_type", label: "Type" },
  { key: "lead_status", label: "Status" },
  { key: "customer_feedback", label: "Customer feedback" },
  { key: "escalation_threat", label: "Escalation threat" },
  { key: "customer_tone", label: "Customer tone" },
  { key: "language_of_call", label: "Language of call" },
  { key: "disposition", label: "Disposition" },
];
const BEHAVIORS = [
  { value: "close", label: "Close the lead" },
  { value: "callback", label: "Callback later" },
  { value: "arabic_callback", label: "Arabic callback" },
  { value: "open", label: "Leave open" },
];

const DEFAULT_COLORS = {
  disposition: {
    DC: "#059669", PC: "#D97706", CB: "#0284C7",
    RF: "#DC2626", NA: "#64748B", WN: "#B91C1C", NR: "#EA580C",
  },
};

export default function DropdownCatalogueManager() {
  const { active } = useClient();
  const isCampaign = active?.kind !== "leads";
  const keys = isCampaign ? CAMPAIGN_KEYS : CATALOGUE_KEYS;
  const [items, setItems] = useState([]);
  const [activeKey, setActiveKey] = useState(isCampaign ? "lead_type" : "sector");
  const [editing, setEditing] = useState(null);     // {id, label, value, color, header, delivery_date}
  const [renameAsk, setRenameAsk] = useState(null); // {row, patch, impact}
  const [form, setForm] = useState({ label: "", value: "", color: "", ownership_group: "" });
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [drag, setDrag] = useState(null);
  const [moveAsk, setMoveAsk] = useState(null);   // {row, group, leads}

  const isDisposition = activeKey === "disposition";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-dropdowns", { params: { key: activeKey } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dropdowns");
    }
  }, [activeKey]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const seen = [...new Set(items.map((i) => i.ownership_group).filter(Boolean))];
    return [...GROUP_ORDER, ...seen.filter((g) => !GROUP_ORDER.includes(g)).sort()];
  }, [items]);

  const add = async () => {
    const label = (form.label || "").trim();
    const value = (form.value || "").trim() || label;
    if (!label) return toast.error("Label is required");
    if (isDisposition && !form.ownership_group) {
      return toast.error("Ownership group is required for a disposition");
    }
    setBusy(true);
    try {
      await api.post("/lead-dropdowns", {
        key: activeKey,
        label, value,
        color: form.color || DEFAULT_COLORS[activeKey]?.[value] || null,
        active: true,
        sort_order: items.length + 1,
        ownership_group: isDisposition ? form.ownership_group : null,
      });
      setForm({ label: "", value: "", color: "", ownership_group: "" });
      toast.success("Added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const saveRow = async (row, patch) => {
    try {
      await api.post("/lead-dropdowns", {
        key: row.key, label: row.label, value: row.value,
        color: row.color || null, active: row.active !== false,
        sort_order: row.sort_order ?? 0,
        ownership_group: row.ownership_group || null,
        ...patch,
      });
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      return false;
    }
  };

  const toggleActive = async (row, active) => {
    if (await saveRow(row, { active })) {
      toast.success(active ? "Visible to agents" : "Hidden from agents");
    }
  };

  const askMoveGroup = async (row, group) => {
    if (group === row.ownership_group) return;
    let leads = 0;
    try {
      const { data } = await api.get("/lead-dropdowns/usage",
        { params: { key: row.key, value: row.value } });
      leads = data.leads || 0;
    } catch { /* count is advisory */ }
    setMoveAsk({ row, group, leads });
  };

  const confirmMove = async () => {
    const { row, group } = moveAsk;
    setMoveAsk(null);
    if (await saveRow(row, { ownership_group: group })) {
      toast.success(`${row.label} moved to ${groupName(group)}`);
    }
  };

  const remove = async (row) => {
    try {
      const { data } = await api.delete("/lead-dropdowns", { params: { key: row.key, value: row.value } });
      toast.success(data.deactivated
        ? `In use by ${data.in_use} lead(s) — deactivated instead of deleted`
        : "Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  /** Reorder within a group by drag-and-drop; persists sort_order for the group. */
  const onDrop = async (targetRow) => {
    if (!drag || drag.value === targetRow.value) return setDrag(null);
    if (isDisposition && drag.ownership_group !== targetRow.ownership_group) return setDrag(null);
    const scope = items
      .filter((i) => !isDisposition || i.ownership_group === targetRow.ownership_group)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const without = scope.filter((i) => i.value !== drag.value);
    const at = without.findIndex((i) => i.value === targetRow.value);
    without.splice(at, 0, drag);
    setDrag(null);
    try {
      await api.post("/lead-dropdowns/reorder", {
        key: activeKey,
        items: without.map((i, idx) => ({ value: i.value, sort_order: idx })),
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  const startEdit = (row) => setEditing({
    id: row.id, label: row.label || "", value: row.value || "", color: row.color || "",
    header: row.header || "", delivery_date: row.delivery_date || "",
  });

  const applyEdit = async (row, patch) => {
    try {
      const { data } = await api.patch(`/lead-dropdowns/${row.id}`, patch);
      const c = data.cascade;
      toast.success(c
        ? `Saved — code ${c.old} → ${c.new}: ${c.leads} lead(s) and ${c.history_entries} history entr${c.history_entries === 1 ? "y" : "ies"} re-pointed`
        : "Option updated");
      setEditing(null); setRenameAsk(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveEdit = async (row) => {
    if (!editing.label.trim()) { toast.error("Label cannot be blank."); return; }
    const newCode = editing.value.trim();
    if (newCode && newCode !== row.value && items.some((i) => i.value === newCode && i.id !== row.id)) {
      toast.error(`Code '${newCode}' already exists in this list.`); return;
    }
    const patch = { label: editing.label.trim() };
    if (newCode && newCode !== row.value) patch.value = newCode;
    if (isDisposition) patch.color = editing.color || null;
    if (activeKey === "lead_status") { patch.header = editing.header; patch.delivery_date = editing.delivery_date; }
    if (patch.value) {
      try {
        const { data } = await api.get(`/lead-dropdowns/${row.id}/rename-impact`);
        setRenameAsk({ row, patch, impact: data });
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not compute impact"); }
      return;
    }
    await applyEdit(row, patch);
  };

  const setBehavior = (row, behavior) => applyEdit(row, { behavior });

  const renderEditRow = (row) => (
    <li key={row.value} className="px-3 py-2 text-xs bg-indigo-50/40 border-l-2 border-indigo-400 space-y-2"
        data-testid={`catalogue-edit-row-${row.value}`}>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Label</Label>
          <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                 className="h-7 text-xs mt-0.5" data-testid="catalogue-edit-label" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Code</Label>
          <Input value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                 className="h-7 text-xs mt-0.5 font-mono" placeholder={row.value} data-testid="catalogue-edit-value" />
        </div>
        {isDisposition ? (
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Colour</Label>
            <input type="color" value={editing.color || "#94A3B8"} onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                   className="h-7 w-10 mt-0.5 block rounded border border-slate-200" data-testid="catalogue-edit-color" />
          </div>
        ) : <div />}
      </div>
      {activeKey === "lead_status" && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Header shown to the agent</Label>
            <Input value={editing.header} onChange={(e) => setEditing({ ...editing, header: e.target.value })}
                   className="h-7 text-xs mt-0.5" data-testid="catalogue-edit-header" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Delivery date ({"{delivery_date}"})</Label>
            <Input value={editing.delivery_date} onChange={(e) => setEditing({ ...editing, delivery_date: e.target.value })}
                   className="h-7 text-xs mt-0.5" data-testid="catalogue-edit-delivery-date" />
          </div>
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <Button size="sm" className="h-6 text-[10px] bg-indigo-600 hover:bg-indigo-700" onClick={() => saveEdit(row)}
                data-testid="catalogue-edit-save"><Check className="w-3 h-3 mr-1" /> Save</Button>
        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEditing(null)}
                data-testid="catalogue-edit-cancel"><X className="w-3 h-3 mr-1" /> Cancel</Button>
      </div>
    </li>
  );

  const rowsFor = (group) => items
    .filter((i) => (group ? i.ownership_group === group : true))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const renderRow = (row) => editing?.id === row.id ? renderEditRow(row) : (
    <li
      key={row.value}
      draggable
      onDragStart={() => setDrag(row)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(row)}
      className={`flex items-center justify-between px-3 py-2 text-xs bg-white ${row.active === false ? "opacity-50" : ""} ${drag?.value === row.value ? "ring-1 ring-indigo-300" : ""}`}
      data-testid={`catalogue-row-${row.value}`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="w-3 h-3 text-slate-300 cursor-grab" />
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0 border border-slate-200"
          style={{ background: row.color || "#94A3B8" }}
        />
        <span className="font-medium text-slate-800">{row.label}</span>
        <Badge variant="outline" className="text-[9px] font-mono">{row.value}</Badge>
        {activeKey === "lead_status" && row.header && (
          <span className="text-[10px] text-slate-500 truncate max-w-[260px]" title={row.header}>· {row.header}</span>
        )}
        {row.active === false && (
          <Badge variant="outline" className="text-[9px] text-slate-400">hidden</Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isDisposition && isCampaign && (
          <Select value={row.behavior || "open"} onValueChange={(v) => setBehavior(row, v)}>
            <SelectTrigger className="h-6 text-[10px] w-[140px]" title="When saved" data-testid={`catalogue-behavior-${row.value}`}>
              <SelectValue placeholder="When saved" />
            </SelectTrigger>
            <SelectContent>
              {BEHAVIORS.map((b) => <SelectItem key={b.value} value={b.value} className="text-xs">{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-indigo-700"
                onClick={() => startEdit(row)} title="Edit label / code" data-testid={`catalogue-edit-${row.value}`}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        {isDisposition && (
          <Select value={row.ownership_group || ""} onValueChange={(v) => askMoveGroup(row, v)}>
            <SelectTrigger className="h-6 text-[10px] w-[150px]" data-testid={`catalogue-group-${row.value}`}>
              <SelectValue placeholder="Group *" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g) => (
                <SelectItem key={g} value={g} className="text-xs">{groupName(g)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Switch
          checked={row.active !== false}
          onCheckedChange={(v) => toggleActive(row, v)}
          className="scale-75"
          title={row.active !== false ? "Visible to agents — click to hide" : "Hidden from agents — click to show"}
          data-testid={`catalogue-active-${row.value}`}
        />
        <Button
          variant="ghost" size="icon"
          className="h-6 w-6 text-rose-500 hover:text-rose-700"
          onClick={() => remove(row)}
          data-testid={`catalogue-delete-${row.value}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );

  return (
    <div className="space-y-4" data-testid="dropdown-catalogue">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-indigo-600" />
            <h3 className="font-heading text-sm font-semibold">Dropdown Catalogue</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Manage the choices agents see in the Lead capture form. Each entry is scoped
            to the currently-active client.
            {isDisposition && " Every disposition must belong to an ownership group — the Assigning Logic matrix acts on these groups."}
          </p>

          {/* Key selector */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="catalogue-keys">
            {keys.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => setActiveKey(k.key)}
                className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                  activeKey === k.key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"
                }`}
                data-testid={`catalogue-key-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>

          {/* Add row */}
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Label</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="h-8 text-xs mt-1"
                data-testid="catalogue-label-input"
                placeholder={`e.g. ${activeKey === "sector" ? "Retail" : "New option"}`}
              />
            </div>
            <div className="w-28">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Code (opt)</Label>
              <Input
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                className="h-8 text-xs mt-1 font-mono"
                data-testid="catalogue-value-input"
                placeholder="auto"
              />
            </div>
            {isDisposition && (
              <div className="w-[190px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  Ownership group <span className="text-rose-500">*</span>
                </Label>
                <Select value={form.ownership_group}
                        onValueChange={(v) => setForm((f) => ({ ...f, ownership_group: v }))}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="catalogue-group-input">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g} value={g} className="text-xs">
                        <span className="flex flex-col">
                          <span>{groupName(g)}</span>
                          <span className="text-[10px] text-slate-400">{GROUP_META[g]?.meaning}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Palette className="w-3 h-3" /> Color
              </Label>
              <Input
                type="color"
                value={form.color || "#64748B"}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                className="h-8 w-14 p-1 cursor-pointer mt-1"
                data-testid="catalogue-color-input"
              />
            </div>
            <Button
              onClick={add}
              disabled={busy || !form.label.trim() || (isDisposition && !form.ownership_group)}
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="catalogue-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          </div>

          {/* List */}
          {items.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-8 border rounded" data-testid="catalogue-empty">
              No entries for <strong>{activeKey}</strong> yet. Add the first one above.
            </div>
          ) : isDisposition ? (
            <div className="space-y-2" data-testid="catalogue-groups">
              {groups.map((g) => {
                const rows = rowsFor(g);
                const open = !collapsed[g];
                return (
                  <div key={g} className="border rounded overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [g]: !!open }))}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left ${GROUP_META[g]?.tint || "bg-indigo-50 text-indigo-800"}`}
                      data-testid={`catalogue-group-header-${g}`}
                    >
                      <span className="flex items-center gap-2">
                        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        <span className="text-xs font-semibold">{groupName(g)}</span>
                        <span className="text-[10px] opacity-70">{GROUP_META[g]?.meaning}</span>
                      </span>
                      <Badge variant="outline" className="text-[9px] bg-white/70"
                             data-testid={`catalogue-group-count-${g}`}>
                        {rows.length}
                      </Badge>
                    </button>
                    {open && (
                      rows.length === 0
                        ? <div className="px-3 py-3 text-[11px] text-slate-400">No dispositions in this group.</div>
                        : <ul className="divide-y divide-slate-100">{rows.map(renderRow)}</ul>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border rounded overflow-hidden">
              <ul className="divide-y divide-slate-100">{rowsFor(null).map(renderRow)}</ul>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!renameAsk} onOpenChange={(o) => { if (!o) setRenameAsk(null); }}>
        <AlertDialogContent data-testid="catalogue-rename-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change code {renameAsk?.row?.value} → {renameAsk?.patch?.value}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every record in this tenant that uses the old code will be re-pointed so reports never split.
              <span className="block font-medium text-slate-700 mt-1" data-testid="catalogue-rename-count">
                {renameAsk?.impact?.leads ?? 0} lead(s) and {renameAsk?.impact?.history_entries ?? 0} history entr{renameAsk?.impact?.history_entries === 1 ? "y" : "ies"} will be updated.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catalogue-rename-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyEdit(renameAsk.row, renameAsk.patch)} data-testid="catalogue-rename-confirm">
              Change code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!moveAsk} onOpenChange={(o) => { if (!o) setMoveAsk(null); }}>
        <AlertDialogContent data-testid="catalogue-move-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Move to {groupName(moveAsk?.group)}?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs space-y-2">
              <span className="block">
                <strong>{moveAsk?.row?.label}</strong> currently sits in {groupName(moveAsk?.row?.ownership_group)}.
                Moving it changes what the Assigning Logic matrix does when an agent saves this
                disposition — ownership may transfer where it previously did not (or vice versa).
              </span>
              <span className="block font-medium text-slate-700" data-testid="catalogue-move-count">
                {moveAsk?.leads ?? 0} lead(s) currently hold this disposition.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catalogue-move-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMove} data-testid="catalogue-move-confirm">
              Move disposition
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
